import React, {
  ReactNode,
  Ref,
} from 'react';
import clsx from 'clsx';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import ActionMenu, {
  DropdownMenuItem,
} from 'core/components/DropdownMenu';
import {
  PopperPlacement,
} from 'core/components/DropdownMenu/arrow';
import Button from 'pages/Dashboard/components/Button';

type Part = 'root' | 'primary' | 'caret';

type Props = {
  label: ReactNode;
  onPrimaryClick: () => void;
  items: DropdownMenuItem[];
  caretDirection?: 'up' | 'down';
  variant?: 'contained' | 'outlined';
  color?: 'primary' | 'secondary' | 'error' | 'inherit';
  disabled?: boolean;
  loading?: boolean;
  startIcon?: ReactNode;
  placement?: PopperPlacement;
  openOnHover?: boolean;
  offset?: [number, number];
  paperClassName?: string;
  showArrow?: boolean;
  disableFlip?: boolean;
  classes?: Partial<Record<Part, string>>;
  primaryRef?: Ref<HTMLButtonElement>;
  disableElevation?: boolean;
};

export default function SplitActionButton({
  label,
  onPrimaryClick,
  items,
  caretDirection = 'down',
  variant = 'contained',
  color = 'primary',
  disabled = false,
  loading = false,
  startIcon,
  placement = 'bottom-end',
  openOnHover = true,
  offset = [0, 6],
  paperClassName,
  showArrow,
  disableFlip = false,
  classes,
  primaryRef,
  disableElevation = false,
}: Props) {
  const CaretIcon = caretDirection === 'up'
    ? KeyboardArrowUpRoundedIcon
    : KeyboardArrowDownRoundedIcon;

  const isDisabled = disabled || loading;
  const hasElevation = variant === 'contained'
    && !disableElevation
    && !isDisabled;

  const dividerClassName = variant === 'contained'
    ? 'bg-blue-600'
    : 'bg-gray-200';

  return (
    <div
      className={clsx(
        'inline-flex items-stretch h-8 rounded-md transition-shadow',
        { 'shadow hover:shadow-md': hasElevation },
        classes?.root,
      )}
    >
      <Button
        ref={primaryRef}
        variant={variant}
        color={color}
        type="button"
        disabled={isDisabled}
        disableElevation
        onClick={onPrimaryClick}
        className={clsx(
          'flex items-center gap-2 !rounded-r-none !h-full !min-h-0 !py-0',
          classes?.primary,
          { '!border-r-0': variant === 'outlined' },
        )}
      >
        {startIcon}
        {label}
      </Button>
      <span
        aria-hidden
        className={clsx(
          'self-stretch w-px pointer-events-none',
          dividerClassName,
          { 'opacity-50 bg-gray-400': isDisabled },
        )}
      />
      <ActionMenu
        trigger={(
          <Button
            variant={variant}
            color={color}
            type="button"
            disabled={isDisabled}
            disableElevation
            className={clsx(
              '!px-1.5 !rounded-l-none !h-full !min-h-0 !py-0',
              classes?.caret,
              { '!border-l-0': variant === 'outlined' },
            )}
          >
            <CaretIcon className="!w-4 !h-4" />
          </Button>
        )}
        items={items}
        openOnHover={openOnHover}
        offset={offset}
        paperClassName={paperClassName}
        placement={placement}
        showArrow={showArrow}
        disableFlip={disableFlip}
      />
    </div>
  );
}
